/**
 * "Does this subscription's product map to anything we grant?" — answered only
 * where the answer is actually knowable (#1701).
 *
 * ## Why this is not a one-line lookup
 *
 * The obvious implementation is "look the product up in the catalog; if it is
 * missing, the subscriber gets nothing". That is true for supapass and FALSE
 * for Stripe, and shipping it would put a red badge on healthy paying
 * subscribers.
 *
 * - **supapass** (and any provider with an `ExternalProviderBundle`) grants via
 *   `getExternalBundle` and nothing else. Absent from the catalog really does
 *   mean it grants nothing. This is the #1715 failure mode, and
 *   `docs/setup/external-subscriptions-setup.md` records a deliberate decision
 *   to run four products uncatalogued with NO alerting, on the stated basis
 *   that an affected record can be found and its product id read off in the
 *   admin console. This function is what keeps that true.
 *
 * - **stripe** used to have a SECOND route: when `getBundleByProductId` missed,
 *   `stripeEventProcessor` fell back to `validatePriceAndGetEntitlement`, which
 *   read `metadata.entitlement` off the live Stripe product. That fallback took
 *   a PRICE id, and the mirror stores product ids only, so it was not
 *   reproducible from a mirror row at all — resolving it would have needed one
 *   Stripe API call per row inside an admin listing. Hence `unknown` here,
 *   never `unmapped`.
 *
 *   **#1811 Step 3 deleted that fallback**, so the reasoning above no longer
 *   holds and a Stripe catalog miss is now genuinely `unmapped` — the catalog
 *   is the only resolver on every grant path. This function has NOT been
 *   changed to say so, deliberately. `isActionableGap` plus `CoverageCell`
 *   would paint every active Stripe row on an uncatalogued product red, and in
 *   production that is the `prod_GkcL61KqM81R5h` population (#1812) — ~20
 *   subscriptions naming member numbers that resolve to nobody. Support would
 *   get a wall of red they cannot act on. #1811 Step 5 makes the switch, and is
 *   gated on #1812 settling what that population is.
 *
 * The distinction is data-driven rather than a hardcoded provider check: a
 * provider is treated as exhaustively catalogued exactly when the API returns
 * an external bundle for it. Apple and Google inherit the correct behaviour
 * from whichever catalog they land in.
 */

import type {
  ExternalProviderBundle,
  SubscriptionBundle,
} from '../../hooks/useAdminEntitlementsList';
import type { AdminSubscriptionInfo } from '../types/subscriptionMirror';

export type CatalogCoverage =
  /** Product matches a catalog entry; `label`/`entitlements` describe it. */
  | { state: 'mapped'; label: string; entitlements: string[] }
  /** Catalog is exhaustive for this provider and does not carry the product. */
  | { state: 'unmapped' }
  /** Not answerable from mirror data — say nothing rather than guess. */
  | { state: 'unknown'; reason: string };

/**
 * Resolve one row against the external catalog.
 *
 * `externalBundles` is the API's per-environment view (display names already
 * merged with this environment's configured ids), so a match here is a match
 * the grant path would also make.
 */
export function resolveCatalogCoverage(
  row: Pick<AdminSubscriptionInfo, 'provider' | 'productIds'>,
  externalBundles: ExternalProviderBundle[] | undefined,
  stripeBundles: SubscriptionBundle[] = []
): CatalogCoverage {
  if (!externalBundles) {
    return { state: 'unknown', reason: 'Catalog not loaded' };
  }

  // An empty string is not a product id. Nothing writes one today — both
  // builders guard it — but a legacy mirror row carrying `['']` would otherwise
  // clear the `length === 0` check, match no bundle, and be reported as a live
  // subscription granting nothing. A false alarm is the one output this module
  // exists to avoid.
  const productIds = row.productIds.filter(pid => !!pid);

  // Checked BEFORE the provider branch: a Stripe row naming no product would
  // otherwise fall out with the catalog-miss wording, describing a lookup that
  // never happened.
  if (productIds.length === 0) {
    return { state: 'unknown', reason: 'Record names no product' };
  }

  const forProvider = externalBundles.filter(b => b.provider === row.provider);
  if (forProvider.length === 0) {
    if (row.provider === 'stripe') {
      // A POSITIVE match is sound: if the product is in SUBSCRIPTION_BUNDLES
      // then `getBundleByProductId` finds it and grants exactly these
      // entitlements. The ABSENCE used to be uninformative, because of the
      // product-metadata fallback; since #1811 Step 3 it is informative and
      // means the subscription grants nothing. This function still reports
      // `unknown` rather than `unmapped` for it — see the module docstring for
      // why that is deferred to Step 5 rather than an oversight.
      //
      // The empty-`stripeProductId` case needs no special handling: the id is
      // read from an env var at module load, and if it is unset every entry is
      // falsy, matches nothing, and every row lands on `unknown` — no claim,
      // rather than a false one.
      const match = stripeBundles.find(
        b => !!b.stripeProductId && productIds.includes(b.stripeProductId)
      );
      if (match) {
        return {
          state: 'mapped',
          label: match.name,
          entitlements: match.entitlements,
        };
      }
    }
    // No claim is safe. See the module docstring.
    return {
      state: 'unknown',
      reason:
        row.provider === 'stripe'
          ? // Says what is actually uncertain. The badge above this reads
            // "Unknown", so a tooltip stating a flat certainty would contradict
            // it: the GRANT is certain, the ACTION is what is being held back.
            // It also carries the same instruction the red `unmapped` badge
            // gives, because a reader who gets this far needs it either way.
            'Not in the bundle catalog, so this subscription grants nothing. Held back from the red "Not in catalog" flag while a known legacy population is expected here — copy the product id from this row and send it to engineering rather than assuming either way.'
          : `No catalog is published for provider "${row.provider}"`,
    };
  }

  // `.find()` rather than collecting every match, deliberately: it mirrors the
  // `.find()` in `getExternalBundle`, so this page names the same bundle the
  // grant path would choose.
  const match = forProvider.find(bundle =>
    productIds.some(pid => bundle.providerProductIds.includes(pid))
  );
  if (match) {
    return {
      state: 'mapped',
      label: match.label,
      entitlements: match.entitlements,
    };
  }
  return { state: 'unmapped' };
}

/**
 * Does an unmapped product on this row mean someone is currently paying for
 * nothing?
 *
 * Scoped to states the provider considers live, matching the summary card this
 * console replaces (`status === 'ACTIVE' && !bundleId`). Historical rows whose
 * product has since left the catalog are the ordinary state of an old
 * collection; flagging them turns the console into a wall of red and buries the
 * one row that matters.
 */
export function isActionableGap(
  row: Pick<AdminSubscriptionInfo, 'normalizedStatus'>,
  coverage: CatalogCoverage
): boolean {
  return (
    coverage.state === 'unmapped' &&
    (row.normalizedStatus === 'active' || row.normalizedStatus === 'grace')
  );
}
